import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import shortid from 'shortid';
import { KEY_RETURN, KEY_SPACE } from 'keycode-js';
import orderBy from 'lodash/orderBy';
import find from 'lodash/find';
import isFunction from 'lodash/isFunction';
import isEqual from 'lodash/isEqual';
import Spinner from '../Spinner';
import Shadow from '../Shadow';
import LiveRegion from '../LiveRegion';
import './Table.css';

export default class Table extends React.Component {
  constructor(props) {
    super(props);
    const direction = props.sortDirection.replace('ending', '');
    const columnSort = props.sortColumn || props.columns[0].accessor;
    this.state = {
      column: columnSort,
      data: props.autosort === true
        ? orderBy(props.data, [columnSort], direction) : props.data,
      direction,
      styling: props.styling,
      minWidth: props.minWidth === 0 ? props.columns.length * 196 : props.minWidth,
      headerAlignment: props.headerAlignment,
    };
    this.handleSort = this.handleSort.bind(this);
  }

  componentWillReceiveProps(newProps) {
    const direction = newProps.sortDirection.replace('ending', '');
    if (newProps.styling !== this.state.styling) {
      this.setState({ styling: newProps.styling });
    }
    if (newProps.minWidth !== this.state.minWidth) {
      this.setState({ minWidth: newProps.minWidth });
    }
    if (!isEqual(newProps.data, this.state.data)) {
      const newData = newProps.autosort === true ? orderBy(newProps.data, [newProps.sortColumn], direction) : newProps.data;
      this.setState({
        data: newData,
        minWidth: newProps.minWidth === 0 ? newProps.columns.length * 196 : newProps.minWidth,
      });
    }
    if (newProps.sortColumn !== this.state.column) {
      const newData = newProps.autosort === true ? orderBy(newProps.data, [newProps.sortColumn], direction) : newProps.data;
      this.setState({ column: newProps.sortColumn, data: newData });
    }
    if (direction !== this.state.direction) {
      const newData = newProps.autosort === true ? orderBy(newProps.data, [newProps.sortColumn], direction) : newProps.data;
      this.setState({ direction, data: newData });
    }
    if (newProps.headerAlignment !== this.state.headerAlignment) {
      this.setState({ headerAlignment: newProps.headerAlignment });
    }
  }

  handleSort(e, clickedColumn, onSortedChange) {
    const { column, data, direction } = this.state;

    if (this.props.autosort === true) {
      if (column !== clickedColumn) {
        this.setState({
          column: clickedColumn,
          data: orderBy(data, [clickedColumn], ['asc']),
          direction: 'asc',
        });
      } else {
        this.setState({
          data: orderBy(data, [clickedColumn], [direction]),
          direction: direction === 'asc' ? 'desc' : 'asc',
        });
      }
    }
    if (onSortedChange) {
      const currentDirection = column !== clickedColumn || direction === 'desc' ? 'asc' : 'desc';
      const sortingObj = {
        direction: currentDirection,
        column: clickedColumn,
      };
      onSortedChange(e, sortingObj);
    }
  }

  static defaultProps = {
    className: '',
    styling: [],
    sortable: false,
    zebra: false,
    bordered: false,
    compact: false,
    ruled: false,
    loading: false,
    onSortedChange: null,
    minWidth: 0,
    sortColumn: null,
    sortDirection: 'asc',
    autosort: true,
    headerAlignment: 'left',
    caretColor: '#000',
  };

  render() {
    const {
      data,
      direction,
    } = this.state;
    const {
      className,
      columns,
      bordered,
      sortable,
      loading,
      zebra,
      compact,
      ruled,
      onSortedChange,
      sortDirection,
      sortColumn,
      headerAlignment,
      caretColor,
      autosort,
      minWidth,
      styling,
      data: propsData,
      ...otherProps
    } = this.props;

    const containerClasses = classNames(
      'ng-ui-table-container',
      {
        'loading': loading,
      }
    );

    const tableClasses = classNames(
      'ng-ui-widgets',
      'ng-ui-table',
      {
        'zebra': zebra,
        'compact': compact,
        'ruled': ruled,
        'bordered': bordered,
        'sortable': sortable,
      },
      className,
    );

    const getThClasses = (column) => {
      const isCurrentColumn = column.accessor === this.state.column;
      return classNames({
        'sorted': sortable && isCurrentColumn,
        [direction]: sortable && isCurrentColumn,
        [`headerAlignment${(headerAlignment || 'left').charAt(0).toUpperCase() + (headerAlignment || 'left').slice(1)}`]: true,
      });
    };

    // Generate dynamic styles for custom caret colors
    const dynamicStyles = caretColor && caretColor !== '#000' ? (
      <style>{`
        .ng-ui-table th.desc.sorted div.headerContainer div.caret:before {
          background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="14.04" height="14" viewBox="0 0 24.04 24"><polygon points="18.75 16.03 12.02 7.97 5.29 16.03 18.75 16.03" fill-rule="evenodd" fill="${caretColor.replace(/#/g, '%23')}"/></svg>');
          background-position: center 1px;
          background-repeat: no-repeat;
        }
        .ng-ui-table th.asc.sorted div.headerContainer div.caret:before {
          background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="14.04" height="14" viewBox="0 0 24.04 24"><polygon points="5.29 7.97 12.02 16.03 18.75 7.97 5.29 7.97" fill-rule="evenodd" fill="${caretColor.replace(/#/g, '%23')}"/></svg>');
          background-position: center 1px;
          background-repeat: no-repeat;
        }
      `}</style>
    ) : null;

    return (
      <Shadow minWidth={this.state.minWidth} className={containerClasses}>
        {dynamicStyles}
        <div className="ng-ui-spinner-container">
          {loading && <Spinner role="alertdialog" aria-busy="true" aria-live="assertive" theme="secondary" />}
        </div>
        <table
          aria-readonly
          className={tableClasses}
          {...otherProps}
        >
          <thead>
            <tr>
              {columns.map((column) => {
                const sortableAttrs = {};
                const innerSortableAttrs = {};

                if (sortable) {
                  sortableAttrs['aria-sort'] = (this.state.column === column.accessor ? direction : 'none');
                  innerSortableAttrs.tabIndex = '0';
                  innerSortableAttrs.onClick = (e) => {
                    this.handleSort(e, column.accessor, onSortedChange);
                  };
                  innerSortableAttrs.onKeyDown = (e) => {
                    if (e.keyCode === KEY_RETURN || e.keyCode === KEY_SPACE) {
                      this.handleSort(e, column.accessor, onSortedChange);
                    }
                  };
                  innerSortableAttrs.role = 'button';
                }

                return (
                  <th
                    className={getThClasses(column)}
                    key={`${column.accessor}-${sortable ? 'sortable' : ''}`}
                    scope="col"
                    {...sortableAttrs}
                  >
                    <div className="headerContainer" {...innerSortableAttrs}>
                      <div className="caret">
                        {column.customHeader && isFunction(column.customHeader) 
                          ? column.customHeader() 
                          : column.Header}
                      </div>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data.map((row) => {
              let styleObj = {};
              let style = {};
              if (row.className) {
                style = find(styling, { row: row.className });
                styleObj = {
                  backgroundColor: style && style.backgroundColor ? style.backgroundColor : '',
                  color: style && style.color ? style.color : '',
                };
              }
              return (
                <tr
                  key={shortid.generate()}
                  style={styleObj || {}}
                >
                  {columns.map((column) => {
                    let styleCellObj = {};
                    let classNameCustom = '';
                    if (style) {
                      const styleCell = find(style.cells, { accessor: column.accessor });
                      classNameCustom = styleCell && classNames(styleCell.className);
                      styleCellObj = {
                        backgroundColor: styleCell && styleCell.backgroundColor ? styleCell.backgroundColor : '',
                        color: styleCell && styleCell.color ? styleCell.color : '',
                        textAlign: styleCell && styleCell.alignment ? styleCell.alignment : '',
                      };
                    }
                    return (
                      <td
                        key={`${shortid.generate()}-column`}
                        style={styleCellObj || {}}
                        className={classNameCustom}
                      >
                        {column.cell && isFunction(column.cell)
                          ? column.cell(row)
                          : row[column.accessor]
                        }
                      </td>);
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        {(sortable)
          ? <LiveRegion
            aria-live="assertive"
            message={this.state.direction ? `Sorted by: ${this.state.column} - ${this.state.direction}` : ''}
          />
          : null
        }
      </Shadow>
    );
  }
}



Table.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  styling: PropTypes.array,
  sortable: PropTypes.bool,
  zebra: PropTypes.bool,
  bordered: PropTypes.bool,
  compact: PropTypes.bool,
  ruled: PropTypes.bool,
  loading: PropTypes.bool,
  onSortedChange: PropTypes.func,
  minWidth: PropTypes.number,
  sortColumn: PropTypes.string,
  sortDirection: PropTypes.oneOf(['ascending', 'descending', 'asc', 'desc']),
  autosort: PropTypes.bool,
  headerAlignment: PropTypes.oneOf(['left', 'center', 'right']),
  caretColor: PropTypes.string,
};
